import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Paper, Collapse, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MainGrid from '../../components/Dashboard/MainGrid';
import ChartCard from '../../components/Dashboard/ChartCard';
import DisaggregatedForecastChart from '../../components/Dashboard/DisaggregatedForecastChart';
import ApplianceDisaggregationChart from '../../components/Dashboard/ApplianceDisaggregationChart';
import LastDayDisaggregationChart from '../../components/Dashboard/LastDayDisaggregationChart';
import DisaggregatedFlexibilityChart from '../../components/Dashboard/DisaggregatedFlexibilityChart';

const DisaggregatedForecastsPage = ({ forecastsAPIData, selectedDashboardOption }) => {
    const [aboutExpanded, setAboutExpanded] = useState(false);

    useEffect(() => {
        console.log('DisaggregatedForecastsPage received data:', forecastsAPIData);
    }, [forecastsAPIData]);

    // If forecastsAPIData is not yet loaded
    if (!forecastsAPIData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 3 }}>
                <Typography>Waiting for disaggregated forecast data...</Typography>
            </Box>
        );
    }

    // Check if we have any data available
    const hasAnyData = forecastsAPIData.hasOriginalForecast || forecastsAPIData.hasDisaggregationData || forecastsAPIData.hasLastDayDisaggregationData || forecastsAPIData.hasFlexibilityData;

    if (!hasAnyData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 3 }}>
                <Typography>No disaggregated forecast data available. Please try again later.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 2, mx: 2 }}>
            {/* About Disaggregated Forecasts - Expandable Section */}
            <Box sx={{ mb: 3 }}>
                <Paper
                    elevation={0}
                    sx={{
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        borderRadius: 2,
                        p: 0,
                        border: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2,
                            cursor: 'pointer'
                        }}
                        onClick={() => setAboutExpanded(!aboutExpanded)}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            About Disaggregated Forecasts
                        </Typography>
                        <IconButton>
                            {aboutExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>
                    <Collapse in={aboutExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, pt: 0 }}>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                Disaggregated forecasts provide detailed consumption predictions with high temporal resolution.
                                This method offers granular insights into energy consumption patterns, enabling more precise
                                energy management decisions.
                            </Typography>

                            {forecastsAPIData.hasDisaggregationData && (
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                    The appliance-level breakdown shows how total consumption is distributed across different
                                    household appliances, helping you understand which devices contribute most to your energy usage.
                                </Typography>
                            )}

                            {forecastsAPIData.hasLastDayDisaggregationData && (
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                    The last day comparison chart provides validation of model accuracy by comparing actual
                                    consumption with predicted values at the appliance level. This helps assess the reliability
                                    of disaggregation predictions and identify areas for model improvement.
                                </Typography>
                            )}

                            {forecastsAPIData.hasFlexibilityData && (
                                <Typography variant="body1" color="text.secondary">
                                    The disaggregated flexibility chart shows the percentage of flexibility available at different
                                    times, indicating how much the consumption can be adjusted for demand response purposes. Higher
                                    flexibility percentages represent greater potential for load shifting or reduction.
                                </Typography>
                            )}
                        </Box>
                    </Collapse>
                </Paper>
            </Box>

            <MainGrid>
                {/* Forecast Charts Section */}
                {/* Original Disaggregated Forecast Chart */}
                {forecastsAPIData.hasOriginalForecast && (
                    <Grid item xs={12}>
                        <ChartCard
                            title="Disaggregated Forecast - Total Consumption"
                            subtitle={`Showing detailed consumption forecast data`}
                            tooltip="This chart displays the disaggregated forecast consumption data with detailed timestamps."
                        >
                            <DisaggregatedForecastChart data={forecastsAPIData.originalForecast} />
                        </ChartCard>
                    </Grid>
                )}

                {/* Appliance-Level Disaggregated Consumption Chart */}
                {forecastsAPIData.hasDisaggregationData && (
                    <Grid item xs={12}>
                        <ChartCard
                            title="Appliance-Level Consumption Breakdown"
                            subtitle={`Showing consumption by individual appliances`}
                            tooltip="This chart shows how the total consumption is broken down across different appliances including TV, Dishwasher, Washing Machine, and Washer Dryer."
                        >
                            <ApplianceDisaggregationChart data={forecastsAPIData.disaggregationData} />
                        </ChartCard>
                    </Grid>
                )}

                {/* Last Day Real vs Predicted Consumption Chart */}
                {forecastsAPIData.hasLastDayDisaggregationData && (
                    <Grid item xs={12}>
                        <ChartCard
                            title="Last Day Real vs Predicted Consumption by Appliance"
                            subtitle={`Comparing actual consumption with appliance-level predictions for the most recent day`}
                            tooltip="This chart compares the real consumption data with the predicted consumption breakdown by individual appliances for the last available day, providing insights into model accuracy."
                        >
                            <LastDayDisaggregationChart data={forecastsAPIData.lastDayDisaggregationData} />
                        </ChartCard>
                    </Grid>
                )}

                {/* Flexibility Analysis Section with Light Grey Background */}
                <Grid item xs={12}>
                    <Paper
                        elevation={0}
                        sx={{
                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                            borderRadius: 2,
                            p: 3,
                            mt: 2,
                        }}
                    >
                        <Typography
                            variant="h5"
                            component="h2"
                            sx={{
                                mb: 3,
                                fontWeight: 600,
                                color: 'text.primary',
                                textAlign: 'center'
                            }}
                        >
                            Flexibility Analysis
                        </Typography>

                        <MainGrid>
                            {/* Disaggregated Flexibility Chart */}
                            {forecastsAPIData.hasFlexibilityData && (
                                <Grid item xs={12}>
                                    <ChartCard
                                        title="Disaggregated Flexibility Forecast"
                                        subtitle={`Showing flexibility percentage over time for appliance-level consumption`}
                                        tooltip="This chart displays the flexibility percentage available at different times, indicating how much the consumption can be adjusted for demand response purposes."
                                    >
                                        <DisaggregatedFlexibilityChart data={forecastsAPIData.flexibilityData} />
                                    </ChartCard>
                                </Grid>
                            )}


                        </MainGrid>
                    </Paper>
                </Grid>
            </MainGrid>
        </Box>
    );
};

export default DisaggregatedForecastsPage; 